/**
 * Modal Management System
 *
 * This module provides a centralized way to manage modals in the app.
 * It allows React components to open modals in a consistent way.
 *
 * Usage from React:
 *   const { openModal } = useModalManager();
 *   openModal(ModalType.BOOST_PURCHASE, { boostType: BoostTypeEnum.Explosion });
 *   openModal(ModalType.SPECIAL_DEALS, { onPurchaseSuccess: callback, hideShopButton: false });
 *   openModal(ModalType.ENERGY_POPUP, { onClose: callback });
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { BoostPurchaseDialog } from "../../components/BoostPurchaseDialog/BoostPurchaseDialog";
import { SpecialDealsModal } from "../../components/SpecialDealsModal/SpecialDealsModal";
import { EnergyPopup } from "../../components/EnergyPopup/EnergyPopup";
import { Onboarding } from "../../components/Onboarding/Onboarding";
import { BoostTypeEnum } from "../../handlers/types";
import { StorageService } from "../../services/LocalStorageService";
import { backButton } from "@telegram-apps/sdk";

// Modal types
export enum ModalType {
  BOOST_PURCHASE = "BOOST_PURCHASE",
  SPECIAL_DEALS = "SPECIAL_DEALS",
  ENERGY_POPUP = "ENERGY_POPUP",
  ONBOARDING = "ONBOARDING",
}

// Modal data interfaces
interface BoostPurchaseModalData {
  boostType:
    | BoostTypeEnum.Explosion
    | BoostTypeEnum.Heal
    | BoostTypeEnum.Revive;
  onPurchaseSuccess?: () => void;
}

interface SpecialDealsModalData {
  onPurchaseSuccess?: () => void;
  hideShopButton?: boolean;
}

interface EnergyPopupModalData {
  onClose?: () => void;
}

interface OnboardingModalData {
  // No additional data needed
}

type ModalData =
  | BoostPurchaseModalData
  | SpecialDealsModalData
  | EnergyPopupModalData
  | OnboardingModalData;

interface ModalState {
  type: ModalType | null;
  data: ModalData | null;
  isOpen: boolean;
}

interface ModalManagerContextType {
  modalState: ModalState;
  openModal: (type: ModalType, data: ModalData) => void;
  closeModal: () => void;
}

const ModalManagerContext = createContext<ModalManagerContextType | null>(null);

interface ModalManagerProviderProps {
  children: ReactNode;
}

export function ModalManagerProvider({ children }: ModalManagerProviderProps) {
  const [modalState, setModalState] = useState<ModalState>({
    type: null,
    data: null,
    isOpen: false,
  });

  const openModal = useCallback((type: ModalType, data: ModalData) => {
    setModalState({
      type,
      data,
      isOpen: true,
    });

    // Register back button handler when modal opens
    try {
      if (backButton.isSupported()) {
        backButton.show();
        const handleBackButton = () => {
          closeModal();
          if (backButton.isSupported()) {
            backButton.hide();
          }
        };
        backButton.onClick(handleBackButton);
      }
    } catch (error) {
      console.warn("Failed to setup back button:", error);
    }
  }, []);

  const closeModal = useCallback(() => {
    setModalState({
      type: null,
      data: null,
      isOpen: false,
    });

    // Hide back button when modal closes
    try {
      if (backButton.isSupported()) {
        backButton.hide();
        backButton.offClick(() => {});
      }
    } catch (error) {
      console.warn("Failed to hide back button:", error);
    }
  }, []);

  const contextValue: ModalManagerContextType = {
    modalState,
    openModal,
    closeModal,
  };

  return (
    <ModalManagerContext.Provider value={contextValue}>
      {children}
      <ModalRenderer />
    </ModalManagerContext.Provider>
  );
}

function ModalRenderer() {
  const { modalState, closeModal } = useModalManager();

  if (!modalState.isOpen || !modalState.type || !modalState.data) {
    return null;
  }

  switch (modalState.type) {
    case ModalType.BOOST_PURCHASE: {
      const data = modalState.data as BoostPurchaseModalData;
      return (
        <BoostPurchaseDialog
          isOpen={true}
          onClose={closeModal}
          boostType={data.boostType}
          onPurchaseSuccess={() => {
            data.onPurchaseSuccess?.();
            closeModal();
          }}
        />
      );
    }

    case ModalType.SPECIAL_DEALS: {
      const data = modalState.data as SpecialDealsModalData;
      return (
        <SpecialDealsModal
          isOpen={true}
          onClose={closeModal}
          onPurchaseSuccess={() => {
            data.onPurchaseSuccess?.();
            closeModal();
          }}
          hideShopButton={data.hideShopButton}
        />
      );
    }

    case ModalType.ENERGY_POPUP: {
      const data = modalState.data as EnergyPopupModalData;
      return (
        <EnergyPopup
          isOpen={true}
          onClose={() => {
            data.onClose?.();
            closeModal();
          }}
        />
      );
    }

    case ModalType.ONBOARDING: {
      return <Onboarding isOpen={true} onClose={closeModal} />;
    }

    default:
      return null;
  }
}

export function useModalManager() {
  const context = useContext(ModalManagerContext);
  if (!context) {
    throw new Error(
      "useModalManager must be used within a ModalManagerProvider"
    );
  }
  return context;
}

// Helper functions for opening modals from outside React context
export function showBoostPurchaseDialog(
  boostType:
    | BoostTypeEnum.Explosion
    | BoostTypeEnum.Heal
    | BoostTypeEnum.Revive,
  onPurchaseSuccess?: () => void
): void {
  // This is a simplified version that will need to be called from React context
  console.warn(
    "showBoostPurchaseDialog should be called from React context using useModalManager"
  );
}

export function showSpecialDealsModal(
  onPurchaseSuccess?: () => void,
  hideShopButton?: boolean
): void {
  console.warn(
    "showSpecialDealsModal should be called from React context using useModalManager"
  );
}

export function showEnergyPopup(onClose?: () => void): void {
  console.warn(
    "showEnergyPopup should be called from React context using useModalManager"
  );
}

export function showOnboarding(): void {
  console.warn(
    "showOnboarding should be called from React context using useModalManager"
  );
}
